import { useEffect, useState } from 'react';
import type { PayableInvoice } from '../../types/payments';
import { formatCurrency } from '../../lib/format';
import { formatCardRef, formatElapsed, formatPropertyLabel } from '../../lib/payments';
import { Spinner } from '../Spinner';

interface WaitingForApprovalProps {
  invoice: PayableInvoice;
  /** Epoch ms when the payment was initiated — the clock starts here. */
  startedAt: number;
  /** Last four of the charged card, from the polled attempt — null until the first poll lands. */
  cardLast4?: string | null;
  /** Set when polling the service is failing; the wait itself continues. */
  pollError?: string | null;
  now?: () => number;
}

/**
 * The 2FA wait (CHO-754) — the state that feels broken when done badly. The
 * operator has switched to their phone to approve the e-banking push; this
 * panel tells them that in plain words, shows the wait is live via an elapsed
 * clock, and never presents a bare unexplained spinner.
 */
export function WaitingForApproval({ invoice, startedAt, cardLast4 = null, pollError = null, now = () => Date.now() }: WaitingForApprovalProps) {
  const [elapsedMs, setElapsedMs] = useState(() => now() - startedAt);

  useEffect(() => {
    const tick = setInterval(() => setElapsedMs(now() - startedAt), 1000);
    return () => clearInterval(tick);
  }, [startedAt, now]);

  return (
    <div className="bg-white border border-sky-200 rounded-lg p-6 max-w-xl" aria-live="polite">
      <div className="flex items-start gap-4">
        <Spinner size={28} className="mt-1 shrink-0" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Waiting for your approval</h2>
          <p className="text-sm text-gray-700 mt-1">
            Approval is now expected in your e-banking app — open it on your phone and approve the payment push
            notification.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {formatCurrency(invoice.amount, invoice.currency)} to {invoice.biller ?? '—'} ({formatPropertyLabel(invoice)})
            {cardLast4 && (
              <>, card {formatCardRef(cardLast4)}</>
            )}
            .
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-2 text-sm text-gray-600">
        <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-sky-500" />
        </span>
        <span>
          Waiting for <span className="font-mono font-medium text-gray-900">{formatElapsed(elapsedMs)}</span> — this
          page updates on its own; leave it open.
        </span>
      </div>

      {pollError && (
        <p role="alert" className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          {pollError}
        </p>
      )}
    </div>
  );
}
